import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

import { DemoApp } from './DemoApp';
import { createDemoWorkerHost, type DemoStageHost, type DemoWorkerHostDeps } from './demo-worker-host';
import type { Cue } from './cues';
import type { DemoStageDriver } from './stage-driver';
import type { FullscreenApi } from './fullscreen';
import type { SessionControllerDeps } from '../SessionController';
import type { AmbientRenderer } from '../../ui/home/AmbientBackground';
import { Z } from './z-layers';

export interface PresentationDemoRootProps {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  origin?: string;
  fullscreen?: FullscreenApi;
  cueList?: readonly Cue[];
  createRenderer?: (container: HTMLElement, tickRateHz: number) => AmbientRenderer;
  createStageHost?: (deps: DemoWorkerHostDeps) => DemoStageHost;
  transportFactory: SessionControllerDeps['transportFactory'];
  scheduler?: SessionControllerDeps['scheduler'];
  rng?: SessionControllerDeps['rng'];
}

/** Production `/demo` root after the presentation hand-off. It keeps the original worker-backed
 * B++ chapters, but replaces the former secret/tournament bootstrap with Live Match rendezvous. */
export function PresentationDemoRoot({
  baseUrl,
  fetchImpl,
  origin,
  fullscreen,
  cueList,
  createRenderer,
  createStageHost = createDemoWorkerHost,
  transportFactory,
  scheduler,
  rng,
}: PresentationDemoRootProps): ReactElement {
  const listenersRef = useRef(new Set<(json: string) => void>());
  const hostRef = useRef<DemoStageHost | null>(null);
  const [stageDead, setStageDead] = useState(false);

  const ensureHost = useCallback((): DemoStageHost => {
    hostRef.current ??= createStageHost({
      onSnapshot: (json) => { for (const listener of listenersRef.current) listener(json); },
      onRefused: (event) => {
        console.warn('waggle: the demo stage reported', event);
        if (event.kind === 'unavailable') setStageDead(true);
      },
    });
    return hostRef.current;
  }, [createStageHost]);

  const createStageDriver = useCallback((): DemoStageDriver => ({
    configure: (stage) => ensureHost().configure(stage),
    spawn: (op) => ensureHost().spawn(op),
    hotSwap: (op) => ensureHost().hotSwap(op),
    fastForward: (ticks) => ensureHost().fastForward(ticks),
    command: (op) => ensureHost().command(op),
    submitCommand: (op) => ensureHost().submitCommand(op),
    createType: (op) => ensureHost().createType(op),
    vocabulary: () => ensureHost().vocabulary?.() ?? Promise.resolve([]),
    worldForInstruct: () => ensureHost().worldForInstruct?.() ?? Promise.resolve({ fieldSizeU: 0, hive: { x: 0, y: 0 }, roster: [], patches: [] }),
  }), [ensureHost]);

  useEffect(() => () => { hostRef.current?.close(); hostRef.current = null; }, []);
  const subscribe = useCallback((listener: (json: string) => void) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  return <DemoApp
    createStageDriver={createStageDriver}
    fullscreen={fullscreen}
    cueList={cueList}
    stage={{ subscribe, createRenderer }}
    // The agent bars default to the bare global fetch when no seam is given, which on an
    // Entra-gated deployment posts /demo/instruct and the practice-chat routes with no tenant
    // token — refused there, invisible locally. The root's fetchImpl is the bearer-decorated one.
    agents={{ baseUrl, instruct: { fetchImpl }, beekeeper: { fetchImpl } }}
    presentation={{
      baseUrl, fetchImpl, origin, transportFactory, scheduler, rng,
      createRenderer: createRenderer as SessionControllerDeps['createRenderer'],
    }}
    overlay={stageDead ? <div role="alert" style={FAILURE}>The stage isn’t running on this machine — the world will stay empty.</div> : undefined}
  />;
}

const FAILURE = { position: 'fixed', left: 0, right: 0, bottom: 0, padding: 16, textAlign: 'center', zIndex: Z.stageFailure, background: 'var(--danger)', color: 'var(--surface-card)' } as const;
